import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react"
import { fmtQty } from "./ronda-api"

/** O que o servidor devolve para o aviso (`serialActivationPreview` + a série lida). */
export interface SerialActivationAsk {
  product_id: number
  product_name: string | null
  /** Por modelo: `none`, `serial` (já tem — nada a perguntar) ou `lot`. */
  tracking: string
  storable?: boolean
  no_lot_qty: number
  /** Entregas/transferências em curso (distintas) que vão passar a pedir série. */
  open_moves: number
  serial: string | null
  /** Ronda: quantidade já contada sem série neste artigo (sai ao ativar). */
  counted_qty?: number
  /** Receção: quantas séries deste artigo há no documento. */
  serial_count?: number
}

/**
 * O aviso de «ativar o número de série» (0.35.0) — o mesmo na ronda e na
 * receção. Quem está a ler decide, e nunca por omissão:
 *  - ronda: «Ativar e guardar a série» / «Cancelar» (o código não conta);
 *  - receção: «Ativar e guardar as séries» / «Seguir sem séries no Odoo»
 *    (uma decisão, fica registada) / «Fechar» (não decide nada).
 * Num artigo com lotes não se oferece ativar; num que já tem séries no Odoo
 * não se pergunta — as séries seguem.
 *
 * Por cima de tudo (`z-[100]`): na ronda abre-se com a câmara aberta.
 */
export function SerialActivationDialog({
  ask,
  context,
  busy,
  error,
  onConfirm,
  onDecline,
  onClose,
}: {
  ask: SerialActivationAsk
  context: "ronda" | "rececao"
  busy: boolean
  error?: string | null
  onConfirm: () => void
  /** Ronda: «Cancelar». Receção: «Seguir sem séries no Odoo» (grava a decisão). */
  onDecline: () => void
  /** Receção: «Fechar» — sai sem decidir. */
  onClose?: () => void
}) {
  const name = ask.product_name ?? `Artigo ${ask.product_id}`
  const moves =
    ask.open_moves > 0
      ? `Há ${ask.open_moves} entrega(s)/transferência(s) em curso deste artigo que vão passar a pedir série.`
      : "Não há entregas nem transferências em curso deste artigo."
  const stock =
    context === "ronda"
      ? "As unidades sem série que lerem recebem a série; as que não tiverem etiqueta ficam por resolver."
      : "As séries desta receção seguem para o Odoo. As unidades que já estão em stock sem série recebem as séries numa ronda."
  const lot = ask.tracking === "lot"
  const already = ask.tracking === "serial"
  const declineLabel = context === "ronda" ? (lot ? "Fechar" : "Cancelar") : "Seguir sem séries no Odoo"
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-3 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="serial-activation-title">
      <div className="w-full max-w-md space-y-3 rounded-2xl bg-background p-4 text-foreground shadow-xl">
        <div className="flex items-start gap-2">
          {already ? <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" /> : <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />}
          <h2 id="serial-activation-title" className="text-lg font-semibold leading-snug">
            {already ? "Já tem número de série" : lot ? "Artigo com lotes" : "Ativar o número de série?"}
          </h2>
        </div>
        {already ? (
          <p className="text-base leading-relaxed">
            <span className="font-semibold">{name}</span> já tem número de série no Odoo — as séries seguem.
          </p>
        ) : lot ? (
          <p className="text-base leading-relaxed">
            <span className="font-semibold">{name}</span> usa lotes no Odoo, não séries: a app não lhe muda o rastreio.{" "}
            {context === "ronda" ? "O código não conta." : "As séries desta linha não vão para o Odoo."}
          </p>
        ) : (
          <p className="text-base leading-relaxed">
            <span className="font-semibold">{name}</span> ainda não tem número de série no Odoo. Passa a ter? {moves} {stock}
          </p>
        )}
        {!already && context === "ronda" && (ask.counted_qty ?? 0) > 0 && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Já contou {fmtQty(ask.counted_qty ?? 0)} em quantidade neste artigo nesta ronda: essa contagem sai — conte lendo a série de cada unidade.
          </p>
        )}
        {ask.serial && (
          <p className="text-sm text-muted-foreground">
            {(ask.serial_count ?? 1) > 1 ? "Séries: " : "Série lida: "}
            <span className="break-all font-mono font-semibold text-foreground">{ask.serial}</span>
            {(ask.serial_count ?? 1) > 1 && ` e mais ${(ask.serial_count ?? 1) - 1}`}
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex flex-col gap-2 pt-1">
          {already ? (
            <button
              type="button"
              disabled={busy}
              onClick={onClose ?? onDecline}
              className="flex h-14 w-full items-center justify-center rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50"
            >
              Continuar
            </button>
          ) : (
            <>
              {!lot && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={onConfirm}
                  className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {busy && <Loader2 className="h-5 w-5 animate-spin" />} {context === "ronda" ? "Ativar e guardar a série" : "Ativar e guardar as séries"}
                </button>
              )}
              {!(context === "ronda" && lot && onClose) && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={onDecline}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border text-base font-medium active:bg-muted disabled:opacity-50"
                >
                  {declineLabel}
                </button>
              )}
              {onClose && (
                <button type="button" disabled={busy} onClick={onClose} className="h-11 w-full rounded-xl text-sm font-medium text-muted-foreground active:bg-muted disabled:opacity-50">
                  Fechar — decidir depois
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
